import { Injectable } from '@angular/core';
import { ShipmentFilterService } from '../services/shipment-filter.service';
import { FilterAssistQuery } from '../../../interfaces/filter-assist-query';
import { ShipmentQuery } from '../../../models/shipment-query';
import { FilterAssist } from '../../../models/filter-assist';
import { DateService } from '../services/date.service';
import { Clock } from '../../../utils/clock';
import { Observable, of } from 'rxjs';
import { FilterCategoryEnum } from '../../../enum/filter-category.enum';

@Injectable({
    providedIn: 'root'
})
export class ShipmentDateFilterService implements FilterAssistQuery<ShipmentQuery> {

    private SHIPMENT_DATE_AFTER = 'SHIPMENT_DATE_AFTER';
    private SHIPMENT_DATE_BEFORE = 'SHIPMENT_DATE_BEFORE';
    private SHIPMENT_DATE_BETWEEN = 'SHIPMENT_DATE_BETWEEN';
    private SHIPMENT_DATE_ON = 'SHIPMENT_DATE_ON';
    private SHIPMENT_DATE_WITHIN_LAST = 'SHIPMENT_DATE_WITHIN_LAST';
    private SHIPMENT_DATE_UNTIL_LAST = 'SHIPMENT_DATE_UNTIL_LAST';
    private SHIPMENT_DATE_WITHIN_NEXT = 'SHIPMENT_DATE_WITHIN_NEXT';
    private SHIPMENT_DATE_AFTER_NEXT = 'SHIPMENT_DATE_AFTER_NEXT';

    constructor(private shipmentFilterService: ShipmentFilterService, private dateService: DateService) {
        shipmentFilterService.registerFilters(this);
    }

    protected getDatesFromString(s: string): string[] {
        const range = this.dateService.interpretUserDateInput(s);
        if (!range.valid) {
            return [];
        }
        return range.period ? [ range.period ] : range.rangeEnd ? [ range.rangeStart, range.rangeEnd ] : [ range.rangeStart ];
    }

    active(query: ShipmentQuery): Observable<FilterAssist[]> {
        const output = [];
        if (query.afterDate && query.beforeDate && !query.afterDate.match('P') && !query.beforeDate.match('P')) {
            output.push(query.afterDate === Clock.advanceDaysIso(query.beforeDate, -1) ?
                this.SHIPMENT_DATE_ON : this.SHIPMENT_DATE_BETWEEN);
        } else {
            if (query.afterDate) {
                if (query.afterDate.startsWith('+P')) {
                    output.push(this.SHIPMENT_DATE_AFTER_NEXT);
                } else if (query.afterDate.startsWith('-P')) {
                    output.push(this.SHIPMENT_DATE_WITHIN_LAST);
                } else {
                    output.push(this.SHIPMENT_DATE_AFTER);
                }
            }
            if (query.beforeDate) {
                if (query.beforeDate.startsWith('+P')) {
                    output.push(this.SHIPMENT_DATE_WITHIN_NEXT);
                } else if (query.beforeDate.startsWith('-P')) {
                    output.push(this.SHIPMENT_DATE_UNTIL_LAST);
                } else {
                    output.push(this.SHIPMENT_DATE_BEFORE);
                }
            }
        }
        return of(output.map((id) => ({
            id,
            description: this.description(id, query.afterDate, query.beforeDate),
            parameter: [ query.afterDate, query.beforeDate ],
            score: 0,
            category: FilterCategoryEnum.SHIPMENT_DATE
        })));
    }

    protected parametersFromTerms(id: string, terms: string): string[] {
        const dates = this.getDatesFromString(terms);
        switch (id) {
            case this.SHIPMENT_DATE_AFTER:
                return [ dates[0], null ];
            case this.SHIPMENT_DATE_BEFORE:
                return [ null, dates[0] ];
            case this.SHIPMENT_DATE_ON:
                return [ dates[0], Clock.advanceDaysIso(dates[0]) ];
            case this.SHIPMENT_DATE_BETWEEN:
                return [ dates[0], dates[1] ];
            case this.SHIPMENT_DATE_WITHIN_LAST:
                return [ '-' + dates[0], null ];
            case this.SHIPMENT_DATE_UNTIL_LAST:
                return [ null, '-' + dates[0] ];
            case this.SHIPMENT_DATE_WITHIN_NEXT:
                return [ null, '+' + dates[0] ];
            case this.SHIPMENT_DATE_AFTER_NEXT:
                return [ '+' + dates[0], null ];
        }
    }

    apply(id: string, parameter: any, query: ShipmentQuery): void {
        if (parameter[0]) {
            query.afterDate = parameter[0];
        }
        if (parameter[1]) {
            query.beforeDate = parameter[1];
        }
    }

    protected description(id: string, afterDate: string, beforeDate: string): string {
        const afterPeriod = afterDate ? afterDate.match(/P(\d+)([YMDW])/) : '';
        const beforePeriod = beforeDate ? beforeDate.match(/P(\d+)([YMDW])/) : '';
        const date1 = afterPeriod || !afterDate ? '' : this.dateService.renderShortDate(afterDate);
        const date2 = beforePeriod || !beforeDate ? '' : this.dateService.renderShortDate(beforeDate);
        let count;
        switch (id) {
            case this.SHIPMENT_DATE_AFTER:
                return $localize`Shipping after ${date1}`;
            case this.SHIPMENT_DATE_BEFORE:
                return $localize`Shipping before ${date2}`;
            case this.SHIPMENT_DATE_ON:
                return $localize`Shipping on ${date1}`;
            case this.SHIPMENT_DATE_BETWEEN:
                return $localize`Shipping between ${date1} and ${date2}`;
            case this.SHIPMENT_DATE_WITHIN_LAST:
                count = afterPeriod[1];
                switch (afterPeriod[2]) {
                    case 'Y':
                        return $localize`Shipping within the last ${count} years`;
                    case 'M':
                        return $localize`Shipping within the last ${count} months`;
                    case 'D':
                        return $localize`Shipping within the last ${count} days`;
                    case 'W':
                        return $localize`Shipping within the last ${count} weeks`;
                }
                break;
            case this.SHIPMENT_DATE_UNTIL_LAST:
                count = beforePeriod[1];
                switch (beforePeriod[2]) {
                    case 'Y':
                        return $localize`Shipping before the last ${count} years`;
                    case 'M':
                        return $localize`Shipping before the last ${count} months`;
                    case 'D':
                        return $localize`Shipping before the last ${count} days`;
                    case 'W':
                        return $localize`Shipping before the last ${count} weeks`;
                }
                break;
            case this.SHIPMENT_DATE_WITHIN_NEXT:
                count = afterPeriod[1];
                switch (afterPeriod[2]) {
                    case 'Y':
                        return $localize`Shipping within the next ${count} years`;
                    case 'M':
                        return $localize`Shipping within the next ${count} months`;
                    case 'D':
                        return $localize`Shipping within the next ${count} days`;
                    case 'W':
                        return $localize`Shipping within the next ${count} weeks`;
                }
                break;
            case this.SHIPMENT_DATE_AFTER_NEXT:
                count = beforePeriod[1];
                switch (beforePeriod[2]) {
                    case 'Y':
                        return $localize`Shipping after the next ${count} years`;
                    case 'M':
                        return $localize`Shipping after the next ${count} months`;
                    case 'D':
                        return $localize`Shipping after the next ${count} days`;
                    case 'W':
                        return $localize`Shipping after the next ${count} weeks`;
                }
                break;
        }
        return `Unrecognized Filter "${id}"`;
    }

    ids(): string[] {
        return [
            this.SHIPMENT_DATE_AFTER,
            this.SHIPMENT_DATE_BEFORE,
            this.SHIPMENT_DATE_ON,
            this.SHIPMENT_DATE_BETWEEN,
            this.SHIPMENT_DATE_WITHIN_LAST,
            this.SHIPMENT_DATE_UNTIL_LAST,
            this.SHIPMENT_DATE_AFTER_NEXT,
            this.SHIPMENT_DATE_WITHIN_NEXT
        ];
    }

    match(terms: string): Observable<FilterAssist[]> {
        const dates = this.getDatesFromString(terms);
        if (dates.length === 0) {
            return of([]);
        }
        if (dates[0].match(/P(\d+)([YMDW])/)) {
            return of([
                {
                    id: this.SHIPMENT_DATE_WITHIN_LAST,
                    description: this.description(this.SHIPMENT_DATE_WITHIN_LAST, dates[0], dates[0]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_WITHIN_LAST, terms),
                    score: 50,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                },
                {
                    id: this.SHIPMENT_DATE_UNTIL_LAST,
                    description: this.description(this.SHIPMENT_DATE_UNTIL_LAST, dates[0], dates[0]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_UNTIL_LAST, terms),
                    score: 65,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                },
                {
                    id: this.SHIPMENT_DATE_WITHIN_NEXT,
                    description: this.description(this.SHIPMENT_DATE_WITHIN_NEXT, dates[0], dates[0]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_WITHIN_NEXT, terms),
                    score: 55,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                },
                {
                    id: this.SHIPMENT_DATE_AFTER_NEXT,
                    description: this.description(this.SHIPMENT_DATE_AFTER_NEXT, dates[0], dates[0]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_AFTER_NEXT, terms),
                    score: 75,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                },
            ]);
        }
        const output = [];
        if (dates.length === 2) {
            if (dates[0] === dates[1]) {
                output.push({
                    id: this.SHIPMENT_DATE_ON,
                    description: this.description(this.SHIPMENT_DATE_ON, dates[0], dates[0]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_ON, terms),
                    score: 50,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                });
            } else {
                output.push({
                    id: this.SHIPMENT_DATE_BETWEEN,
                    description: this.description(this.SHIPMENT_DATE_BETWEEN, dates[0], dates[1]),
                    parameter: this.parametersFromTerms(this.SHIPMENT_DATE_BETWEEN, terms),
                    score: 50,
                    category: FilterCategoryEnum.SHIPMENT_DATE
                });
            }
        }
        if (dates.length === 1 || dates[0] === dates[1]) {
            output.push({
                id: this.SHIPMENT_DATE_AFTER,
                description: this.description(this.SHIPMENT_DATE_AFTER, dates[0], dates[0]),
                parameter: this.parametersFromTerms(this.SHIPMENT_DATE_AFTER, terms),
                score: 40,
                category: FilterCategoryEnum.SHIPMENT_DATE
            });
            output.push({
                id: this.SHIPMENT_DATE_BEFORE,
                description: this.description(this.SHIPMENT_DATE_BEFORE, dates[0], dates[0]),
                parameter: this.parametersFromTerms(this.SHIPMENT_DATE_BEFORE, terms),
                score: 45,
                category: FilterCategoryEnum.SHIPMENT_DATE
            });
        }
        return of(output);
    }

    unapply(id: string, query: ShipmentQuery): void {
        switch (id) {
            case this.SHIPMENT_DATE_AFTER:
            case this.SHIPMENT_DATE_WITHIN_LAST:
            case this.SHIPMENT_DATE_AFTER_NEXT:
                query.afterDate = null;
                break;
            case this.SHIPMENT_DATE_BEFORE:
            case this.SHIPMENT_DATE_UNTIL_LAST:
            case this.SHIPMENT_DATE_WITHIN_NEXT:
                query.beforeDate = null;
                break;
            case this.SHIPMENT_DATE_ON:
            case this.SHIPMENT_DATE_BETWEEN:
                query.afterDate = null;
                query.beforeDate = null;
                break;
        }
    }
}
